import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { isBrandPro } from "../lib/plan";
import SiteFooter from "./SiteFooter";
import BrandLogo from "./BrandLogo";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { LayoutDashboard, Users, BookmarkCheck, MessageSquare, Settings, LogOut, Shield, Building2, Sparkles, Brain, Kanban, BarChart3, Lock, Users2, Menu } from "lucide-react";

// Brand sidebar stage-grouped nav (Feb 2026 redesign). Mirrors the athlete
// flow: set up → discover athletes → engage → measure.
function buildBrandStages({ unread, isAdmin }) {
  return [
    {
      key: "setup", label: "Set up",
      items: [
        { to: "/app/brand", label: "Dashboard", icon: LayoutDashboard, exact: true },
        { to: "/app/brand/profile", label: "Brand Profile", icon: Building2 },
        { to: "/app/brand/organisation", label: "Organisation", icon: Users2 },
      ],
    },
    {
      key: "discover", label: "Discover athletes",
      items: [
        { to: "/app/brand/directory", label: "Athlete Directory", icon: Users },
        { to: "/app/brand/matcher", label: "AI Matcher", icon: Brain, pro: true },
        { to: "/app/brand/shortlist", label: "Shortlist", icon: BookmarkCheck },
      ],
    },
    {
      key: "engage", label: "Engage",
      items: [
        { to: "/app/brand/campaigns", label: "Campaigns", icon: Kanban, pro: true },
        { to: "/app/inbox", label: "Inbox", icon: MessageSquare, badge: unread },
      ],
    },
    {
      key: "measure", label: "Measure",
      items: [
        { to: "/app/brand/analytics", label: "Analytics", icon: BarChart3, pro: true },
      ],
    },
    {
      key: "account", label: "Account",
      items: [
        { to: "/app/pricing", label: "Upgrade", icon: Sparkles },
        { to: "/app/settings", label: "Settings", icon: Settings },
        ...(isAdmin ? [{ to: "/app/admin", label: "Admin", icon: Shield }] : []),
      ],
    },
  ];
}

export default function BrandDashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const onLogout = async () => { await logout(); navigate("/"); };

  useEffect(() => {
    let t;
    const tick = () => api.get("/conversations/inbox/unread-count")
      .then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    tick();
    t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);

  const isPro = isBrandPro(user);
  const STAGES = buildBrandStages({ unread, isAdmin: !!user?.is_admin });
  // 4 quick-tabs for the mobile bottom bar — one per stage.
  const QUICK_TABS = [
    STAGES[0].items[0],   // Dashboard
    STAGES[1].items[0],   // Athlete Directory
    STAGES[2].items[1],   // Inbox
    STAGES[3].items[0],   // Analytics
  ];

  const renderItem = (n, onClick) => (
    <NavLink key={n.to} to={n.to} end={n.exact} onClick={onClick}
      data-testid={`brand-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center justify-between px-3 py-2.5 text-sm uppercase tracking-widest transition-colors ${
          isActive ? "bg-bg-elev text-volt border-l-2 border-volt" : "text-ink-muted hover:text-white"
        }`
      }>
      <span className="flex items-center gap-3">
        <n.icon className="w-4 h-4" />
        <span className="font-semibold">{n.label}</span>
      </span>
      {n.badge > 0 && (
        <span className="bg-volt text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{n.badge}</span>
      )}
      {n.pro && !isPro && (
        <Lock className="w-3 h-3 text-ink-muted" />
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex bg-bg text-white">
      <aside className="hidden md:flex flex-col w-[260px] border-r border-line shrink-0 sticky top-0 h-screen">
        <Link to="/app/brand" className="px-6 py-5 border-b border-line flex items-center gap-3" data-testid="brand-sidebar-logo">
          <BrandLogo variant="wordmark" size={28} showLink={false} />
          <div className="text-[9px] uppercase tracking-widest text-volt border-l border-line pl-3">For Brands</div>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
          {STAGES.map(stage => (
            <div key={stage.key} data-testid={`brand-nav-stage-${stage.key}`}>
              <div className="px-3 pb-2 text-[10px] tracking-widest uppercase text-volt/80 font-bold">
                {stage.label}
              </div>
              <div className="space-y-1">
                {stage.items.map(n => renderItem(n))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-line p-4">
          <div className="text-xs label-eyebrow mb-1">Signed in as brand</div>
          <div className="text-sm font-medium truncate" data-testid="brand-sidebar-user-name">{user?.full_name || user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-volt mt-1">{(user?.organisation_plan || user?.plan || "free")} plan</div>
          <button onClick={onLogout} data-testid="brand-sidebar-logout-btn" className="mt-3 w-full btn-ghost-line flex items-center justify-center gap-2">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-bg/90 backdrop-blur-xl border-b border-line px-4 py-3 flex justify-between items-center">
        <Link to="/app/brand" className="flex items-center">
          <BrandLogo variant="wordmark" size={20} showLink={false} />
        </Link>
        <div className="flex items-center gap-3">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="brand-mobile-nav-trigger"
                aria-label="Open menu"
                className="text-ink-muted hover:text-white p-1.5 -mr-1.5"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-bg border-line text-white w-[78%] max-w-[320px] p-0 flex flex-col">
              <SheetTitle className="sr-only">Brand navigation</SheetTitle>
              <div className="px-5 pt-6 pb-4 border-b border-line">
                <div className="text-xs label-eyebrow mb-1">Signed in as brand</div>
                <div className="text-sm font-medium truncate" data-testid="brand-mobile-nav-user-name">{user?.full_name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-volt mt-1">{(user?.organisation_plan || user?.plan || "free")} plan</div>
              </div>
              <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-5">
                {STAGES.map(stage => (
                  <div key={stage.key} data-testid={`brand-mobile-nav-stage-${stage.key}`}>
                    <div className="px-3 pb-2 text-[10px] tracking-widest uppercase text-volt/80 font-bold">
                      {stage.label}
                    </div>
                    <div className="space-y-1">
                      {stage.items.map(n => (
                        <NavLink key={n.to} to={n.to} end={n.exact}
                          onClick={() => setMobileNavOpen(false)}
                          data-testid={`brand-mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={({ isActive }) =>
                            `flex items-center justify-between px-3 py-3 text-sm uppercase tracking-widest transition-colors ${
                              isActive ? "bg-bg-elev text-volt border-l-2 border-volt" : "text-ink-muted hover:text-white"
                            }`
                          }>
                          <span className="flex items-center gap-3">
                            <n.icon className="w-4 h-4" />
                            <span className="font-semibold">{n.label}</span>
                          </span>
                          {n.badge > 0 && (
                            <span className="bg-volt text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{n.badge}</span>
                          )}
                          {n.pro && !isPro && (
                            <Lock className="w-3 h-3 text-ink-muted" />
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
              <div className="border-t border-line p-4">
                <button onClick={onLogout} data-testid="brand-mobile-nav-logout-btn" className="w-full btn-ghost-line flex items-center justify-center gap-2">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <button onClick={onLogout} className="text-xs uppercase tracking-widest text-ink-muted" data-testid="brand-mobile-logout">Logout</button>
        </div>
      </div>

      <main className="flex-1 min-w-0 mt-14 md:mt-0">
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-bg border-t border-line grid grid-cols-4">
          {QUICK_TABS.map(n => (
            <NavLink key={n.to} to={n.to} end={n.exact} className={({ isActive }) => `flex flex-col items-center py-2 text-[10px] uppercase tracking-widest ${isActive ? "text-volt" : "text-ink-muted"}`}>
              <n.icon className="w-4 h-4 mb-1" /> {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
        <div className="pb-20 md:pb-0">{children}</div>
        <SiteFooter />
      </main>
    </div>
  );
}
